import React, { useContext, useEffect, useState } from "react";
import StagesAutoComplete from "../shared/autoCompletes/StagesAutoComplete";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useTheme } from "@mui/material/styles";
import "react-toastify/dist/ReactToastify.css";
import { useQueryClient } from "react-query";
import { ApiDataContext } from "../../contexts/ApiDataContext";
import { useAutocomplete } from "../../hooks/forms/FormHooks";
import { useManagementTextField } from "../../hooks/forms/ManagementHooks";
import { useDatePicker } from "../../hooks/forms/useDatePicker";
import useDialog from "../../hooks/dialog/useDialog";
import { useLoadingToast } from "../../hooks/toast/toasts";
import { updateMeetingProject, sendBillingPdf } from "../../services/api/api";
import DatePicker from "../DatePicker";
import CustomAutoComplete from "../shared/autoCompletes/CustomAutoComplete";
import ProvidencesAutoComplete from "../shared/autoCompletes/ProvidencesAutoComplete";
import CustomLoadingButton from "../shared/button/CustomLoadingButton";
import CustomContentDialog from "../shared/dialog/CustomContentDialog";
import { FormatMoney, FormatPower } from "../shared/format/Formats";
import CustomTextField from "../shared/textFields/CustomTextField";
import FormatedCustomTextField from "../shared/textFields/FormatedCustomTextField";

const doubleBoxSx = {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
};

const doubleBoxWidth = "48%";

const isDisabled = false;

const defaultBillingKinds = [{ name: "ENTRADA" }, { name: "VISTORIA" }, { name: "INTEGRAL" }];

export default function ProjectMeeting({
  project,
  initializeLoadingToast,
  updateLoadingToast,
  handleNextProject
}) {
  const { apiData } = useContext(ApiDataContext);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { initializeLoadingToast: initializeToast, updateLoadingToast: updateToast } = useLoadingToast();

  const [firstPayment, setFirstPayment] = useState({
    chargeDate: null,
    paymentDate: null,
  });
  const [secondPayment, setSecondPayment] = useState({
    chargeDate: null,
    paymentDate: null,
  });
  const [billingKinds, setBillingKinds] = useState(defaultBillingKinds);
  const [isSendBillLoading, setIsSendBillLoading] = useState(false);
  const { reset: resetBillingKind, ...billingKind } = useAutocomplete();
  const {
    open: billDialogOpen,
    handleClickOpen: handleBillDialogOpen,
    handleClickClose: handleBillDialogClose,
  } = useDialog();

  useEffect(() => {
    project.payments.forEach((p) => {
      switch (p.paymentKinds.name) {
        case "ENTRADA":
          setFirstPayment({
            chargeDate: p.chargeDate,
            paymentDate: p.paymentDate,
          });
          break;
        case "VISTORIA":
          setSecondPayment({
            chargeDate: p.chargeDate,
            paymentDate: p.paymentDate,
          });
          break;
        case "INTEGRAL":
          setFirstPayment({
            chargeDate: p.chargeDate,
            paymentDate: p.paymentDate,
          });
          setSecondPayment({
            chargeDate: p.chargeDate,
            paymentDate: p.paymentDate,
          });
          break;
        default:
          break;
      }
    });

    // Initialize billing kinds based on payments
    initializeBillingKinds();
  }, [project.payments]);

  const isPaymentStatusValid = (status) =>
    status && status !== "EXPIRED" && status !== "CANCELLED";

  const initializeBillingKinds = () => {
    let tempBillingKinds = defaultBillingKinds;

    if (project.payments && project.payments.length > 0) {
      project.payments.forEach((p) => {
        if (p.paymentKindId === 3 && isPaymentStatusValid(p.status)) {
          tempBillingKinds = [];
          return;
        } else if (p.paymentKindId === 2 && isPaymentStatusValid(p.status)) {
          tempBillingKinds = tempBillingKinds.filter((item) => item.name === "ENTRADA");
        } else if (p.paymentKindId === 1 && isPaymentStatusValid(p.status)) {
          tempBillingKinds = tempBillingKinds.filter((item) => item.name === "VISTORIA");
        }
      });
    }
    setBillingKinds(tempBillingKinds);
  };

  const { reset: resetProjectName, ...projectName } = useManagementTextField(project.name);

  const { reset: resetPDC, ...powerDistributionCompany } = useAutocomplete(
    project.powerDistributionCompanies,
    false
  );

  const { reset: resetEngineer, ...engineer } = useAutocomplete(project.engineers, false);

  const initialStageValue = {
    id: project.stage.stageId,
    stageName: project.stage.stageName,
  };

  const { reset: resetStage, ...stage } = useAutocomplete(initialStageValue, false);

  const initialProvidenceValue = {
    id: project.providence.providenceId,
    providenceName: project.providence.providenceName,
  };

  const { reset: resetProvidence, ...providence } = useAutocomplete(initialProvidenceValue, false);

  const { reset: resetNote, ...note } = useManagementTextField(project?.stage?.note);

  const { reset: resetCondition, ...condition } = useAutocomplete(project.conditions, false);

  const { reset: resetDeadline, ...deadline } = useDatePicker(project.dueDate);

  const [clientValue, setClientValue] = useState(project.clients);

  const handleClientChange = async (event, newValue) => {
    setClientValue(newValue);
  };

  const client = {
    value: clientValue,
    onChange: handleClientChange,
  };

  const { reset: resetPower, ...power } = useManagementTextField(project.power);

  const { reset: resetPrice, ...price } = useManagementTextField(project.price);

  const { reset: resetFirstPayment, ...firstPaymentDate } = useDatePicker(firstPayment.paymentDate);

  const { reset: resetSecondPayment, ...secondPaymentDate } = useDatePicker(
    secondPayment.paymentDate
  );

  const { reset: resetFirstSent, ...firstSentDate } = useDatePicker(project.firstSentDate);

  const { reset: resetSecondSent, ...secondSentDate } = useDatePicker(project.secondSentDate);

  const { reset: resetApprovalDate, ...approvalDate } = useDatePicker(project.approvalDate);

  const { reset: resetInspectionDate, ...inspectionDate } = useDatePicker(project.inspectionDate);

  const { reset: resetFinishDate, ...finishDate } = useDatePicker(project.finishDate);

  const [requestLoading, setRequestLoading] = useState(false);

  const handleUpdateClick = async () => {
    let toastId;
    try {
      setRequestLoading(true);

      toastId = initializeLoadingToast({
        text: `Atualizando Projeto ${project.projectNumber}...`,
      });

      const { status } = await updateMeetingProject(
        project.id,
        projectName.value,
        client?.value?.id,
        powerDistributionCompany?.value?.id,
        engineer?.value?.id,
        project.stage.id,
        project.providence.id,
        stage?.value?.id,
        providence?.value?.id,
        note.value,
        condition.value?.id,
        deadline.value,
        power.value,
        price.value,
        firstSentDate.value,
        secondSentDate.value,
        approvalDate.value,
        inspectionDate.value,
        finishDate.value
      );

      if (status === 200) {
        let updatedIds = [];
        if ("updatedIds" in sessionStorage)
          updatedIds = JSON.parse(sessionStorage.getItem("updatedIds"));

        updatedIds.push(project.id);
        sessionStorage.setItem("updatedIds", JSON.stringify(updatedIds));

        updateLoadingToast({
          id: toastId,
          text: `Projeto ${project.projectNumber} atualizado com sucesso!`,
          type: "success",
        });

        setTimeout(() => handleNextProject(), 500);
      }
    } catch (error) {
      updateLoadingToast({
        id: toastId,
        text: `Erro ao atualizar projeto ${project.projectNumber}`,
        type: "error",
      });

      console.error(error);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleSendBillClick = async () => {
    let sendBillToastId;
    setIsSendBillLoading(true);
    try {
      handleBillDialogClose();
      sendBillToastId = initializeToast({
        text: `Enviando Cobrança...`,
      });

      const { status } = await sendBillingPdf(project.id, billingKind.value.name);

      if (status === 200) {
        updateToast({
          id: sendBillToastId,
          text: `Cobrança em processo de envio ao integrador`,
          type: "success",
        });
        queryClient.invalidateQueries({ queryKey: ["paymentsHistory"] });
        resetBillingKind();
      } else {
        throw new Error();
      }
    } catch (error) {
      updateToast({
        id: sendBillToastId,
        text: error.response?.data?.message ?? `Erro ao enviar cobrança, tente novamente`,
        type: "error",
      });
    } finally {
      setIsSendBillLoading(false);
    }
  };

  const dialogContent = (
    <CustomAutoComplete
      params={billingKind}
      options={billingKinds}
      disabled={isSendBillLoading}
      label="Tipo de Pagamento"
      width="100%"
    />
  );

  return (
    <Paper
      key={project.id}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderRadius: "0.625rem",
        px: 2,
        py: 2,
        boxShadow: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: "1.2em" }}>
          Projeto
          <span style={{ fontWeight: 700 }}> {project.projectNumber}</span>
        </Typography>

        <CustomLoadingButton
          color={theme.palette.info.main}
          hoverColor={theme.palette.info.hover}
          startIcon={<WhatsAppIcon />}
          buttonText="Boleto"
          buttonLoadingText="Gerando..."
          sx={{
            height: 30,
            width: "auto",
            px: 2,
          }}
          disabled={billingKinds.length === 0 || isSendBillLoading}
          handleButtonClick={handleBillDialogOpen}
          requestLoading={isSendBillLoading}
        />
      </Box>

      <Box display="flex" sx={{ p: 2, gap: 3 }}>
        <Box display="flex" flexDirection="column" width="33%" sx={{ mb: 1, gap: 2 }}>
          <CustomTextField
            params={projectName}
            disabled={isDisabled}
            label="Nome do Cliente"
            errorLabel="Nome Incorreto"
            maxLength={50}
            margin={0}
          />

          <CustomAutoComplete
            disabled={isDisabled}
            params={client}
            options={apiData.clients}
            label="Integrador"
            width="100%"
            margin={0}
            pt={0}
          />

          <CustomAutoComplete
            disabled={isDisabled}
            params={powerDistributionCompany}
            options={apiData.powerDistributionCompanies}
            label="Concessionária"
            width="100%"
            margin={0}
            pt={0}
          />

          <CustomAutoComplete
            disabled={isDisabled}
            params={engineer}
            options={apiData.engineers}
            label="Engenheiro"
            width="100%"
            margin={0}
            pt={0}
          />

          <StagesAutoComplete
            disabled={isDisabled}
            params={stage}
            options={apiData.stages}
            label="Etapa"
            width="100%"
          />
        </Box>

        <Box display="flex" flexDirection="column" width="33%" sx={{ mb: 1, gap: 2 }}>
          <ProvidencesAutoComplete
            disabled={isDisabled}
            params={providence}
            options={apiData.providences}
            label="Providencia"
            width="100%"
          />
          <CustomTextField
            params={note}
            disabled={isDisabled}
            label="Observação"
            errorLabel="Observação Incorreta"
            maxLength={200}
            margin={0}
          />
          <Box sx={doubleBoxSx}>
            <CustomAutoComplete
              disabled={isDisabled}
              params={condition}
              options={apiData.conditions}
              label="Situação"
              width={doubleBoxWidth}
              pt={0}
              margin={0}
            />

            <DatePicker
              disabled={isDisabled}
              width={doubleBoxWidth}
              label="Prazo Máximo"
              params={deadline}
            />
          </Box>

          <Box sx={doubleBoxSx}>
            <FormatedCustomTextField
              params={power}
              disabled={isDisabled}
              label="Potência"
              errorLabel="Potência Incorreta"
              inputcomponent={FormatPower}
              required={false}
              width={doubleBoxWidth}
            />
            <FormatedCustomTextField
              params={price}
              disabled={isDisabled}
              label="Valor"
              errorLabel="Valor Incorreto"
              inputcomponent={FormatMoney}
              required={false}
              width={doubleBoxWidth}
            />
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" width="33%" sx={{ gap: 2 }}>
          <Box sx={doubleBoxSx}>
            <DatePicker
              disabled={isDisabled}
              width={doubleBoxWidth}
              label="1º Envio"
              params={firstSentDate}
            />
            <DatePicker
              disabled={isDisabled}
              width={doubleBoxWidth}
              label="2º Envio"
              params={secondSentDate}
            />
          </Box>

          <Box sx={doubleBoxSx}>
            <DatePicker
              disabled={true}
              width={doubleBoxWidth}
              label="1º Pagamento"
              params={firstPaymentDate}
            />
            <DatePicker
              disabled={true}
              width={doubleBoxWidth}
              label="2º Pagamento"
              params={secondPaymentDate}
            />
          </Box>

          <Box sx={doubleBoxSx}>
            <DatePicker
              disabled={isDisabled}
              width={doubleBoxWidth}
              label="Aprovação"
              params={approvalDate}
            />
            <DatePicker
              disabled={isDisabled}
              width={doubleBoxWidth}
              label="Vistoria"
              params={inspectionDate}
            />
          </Box>

          <DatePicker disabled={isDisabled} width="100%" label="Homologação" params={finishDate} />
        </Box>
      </Box>

      <Box
        display="flex"
        width="20%"
        sx={{ alignSelf: "flex-end", justifyContent: "space-between" }}
      >
        <CustomLoadingButton
          buttonText="Atualizar"
          buttonLoadingText="Atualizando..."
          sx={{ width: "100%" }}
          disabled={requestLoading}
          handleButtonClick={handleUpdateClick}
          requestLoading={requestLoading}
        />
      </Box>

      <CustomContentDialog
        open={billDialogOpen}
        handleClickClose={handleBillDialogClose}
        title="Enviar Boleto/Pix"
        content={dialogContent}
        requestLoading={isSendBillLoading}
        handleConfirmationClick={handleSendBillClick}
        confirmationClickText="Enviar"
        requestLoadingText="Enviando..."
        width={500}
        height={100}
      />
    </Paper>
  );
}
